import React, { useCallback, useEffect, useState } from 'react';
import { ExternalLink, FlaskConical, Globe, GlobeLock, LayoutTemplate, Loader2, Plus, Settings2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  builderUrl,
  cmsApi,
  cmsLandingpageApi,
  WEBSITE_BASE_URL,
  type CmsLandingpage,
  type CmsLandingpageEingabe,
} from '../../../../lib/api/cms';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Badge } from '../../../../components/ui/badge';
import { Card, CardContent } from '../../../../components/ui/card';
import LoeschenMitGrundDialog from '../../../../components/ui/loeschen-mit-grund-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../components/ui/dialog';
// Spiegelt das Backend, damit der angezeigte Pfad dem gespeicherten entspricht.
import { slugifyPath } from '../../../../lib/slugify';
import LandingpageVarianten from './LandingpageVarianten';

type Ziel = { id: number; name: string };

const LEER: CmsLandingpageEingabe = { slug: '', title: '', fach_id: null, standort_id: null, kampagne: null, noindex: false };
const auswahl = 'h-9 w-full rounded-md border border-input bg-background px-3 text-sm';

/**
 * Landingpages unter /lp/… (Backend: landingpage_service.py). Gestaltet werden
 * sie im ThunderBuilder auf der Website; hier nur Adresse, Fach, Standort,
 * Kampagne und online/offline. Fach und Standort füllen die Platzhalter
 * {{fach}} und {{standort}} und belegen das Probestunden-Formular vor.
 */
const CmsLandingpagesPage: React.FC = () => {
  const [seiten, setSeiten] = useState<CmsLandingpage[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [ziele, setZiele] = useState<{ faecher: Ziel[]; standorte: Ziel[] }>({ faecher: [], standorte: [] });
  // null = Dialog zu, `neu` = anlegen, sonst die Seite, deren Einstellungen offen sind.
  const [dialog, setDialog] = useState<'neu' | CmsLandingpage | null>(null);
  const [eingabe, setEingabe] = useState<CmsLandingpageEingabe>(LEER);
  const [speichert, setSpeichert] = useState(false);
  const [zuLoeschen, setZuLoeschen] = useState<CmsLandingpage | null>(null);
  const [abTest, setAbTest] = useState<CmsLandingpage | null>(null);

  const laden = useCallback(async () => {
    setLaedt(true);
    try {
      setSeiten(await cmsLandingpageApi.list());
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Landingpages konnten nicht geladen werden');
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => {
    void laden();
    cmsLandingpageApi.ziele().then(setZiele).catch(() => { /* Auswahl bleibt leer */ });
  }, [laden]);

  const pfad = slugifyPath(eingabe.slug || eingabe.title);

  const oeffnen = (ziel: 'neu' | CmsLandingpage) => {
    setEingabe(
      ziel === 'neu'
        ? LEER
        : { slug: ziel.pfad, title: ziel.title, fach_id: ziel.fach_id, standort_id: ziel.standort_id, kampagne: ziel.kampagne, noindex: ziel.noindex },
    );
    setDialog(ziel);
  };

  const speichern = async () => {
    if (!eingabe.title.trim()) {
      toast.error('Der Titel fehlt');
      return;
    }
    setSpeichert(true);
    try {
      if (dialog === 'neu') {
        await cmsLandingpageApi.create({ ...eingabe, slug: pfad, title: eingabe.title.trim() });
        toast.success('Landingpage angelegt — jetzt „Gestalten“');
      } else if (dialog) {
        // Der Pfad bleibt: Links aus Anzeigen zeigen darauf.
        const { title, fach_id, standort_id, kampagne, noindex } = eingabe;
        await cmsLandingpageApi.update(dialog.id, { title, fach_id, standort_id, kampagne, noindex });
        toast.success('Gespeichert');
      }
      setDialog(null);
      await laden();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Speichern ist fehlgeschlagen');
    } finally {
      setSpeichert(false);
    }
  };

  // Fenster sofort öffnen, Ziel erst nach dem Token setzen — sonst blockt der Browser das Popup.
  // Auch für Varianten: Die haben eine eigene Seiten-ID.
  const gestalten = async (seiteId: number) => {
    const fenster = window.open('', '_blank');
    try {
      const { token } = await cmsApi.getBuilderUebergabe();
      if (fenster) fenster.location.href = builderUrl(token, seiteId);
      else window.location.href = builderUrl(token, seiteId);
    } catch (e) {
      fenster?.close();
      toast.error(e instanceof Error ? e.message : 'Der Builder konnte nicht geöffnet werden');
    }
  };

  const umschalten = async (s: CmsLandingpage) => {
    try {
      const neu = await cmsLandingpageApi.update(s.id, { online: s.status !== 'published' });
      setSeiten((alt) => alt.map((x) => (x.id === s.id ? neu : x)));
      toast.success(neu.status === 'published' ? 'Die Seite ist online' : 'Die Seite ist offline');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Das hat nicht geklappt');
    }
  };

  const loeschen = async (s: CmsLandingpage, grund: string) => {
    await cmsLandingpageApi.remove(s.id, grund);
    setSeiten((alt) => alt.filter((x) => x.id !== s.id));
    setZuLoeschen(null);
    toast.success('Landingpage gelöscht');
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Landingpages</h1>
          <p className="text-sm text-muted-foreground">
            Kampagnenseiten für ein Fach, einen Standort oder beides — erreichbar unter <code>/lp/&lt;pfad&gt;</code>, gestaltet im
            visuellen Editor. Texte wie <code>{'{{fach}}'}</code> und <code>{'{{standort}}'}</code> füllt die Seite selbst.
          </p>
        </div>
        <Button type="button" onClick={() => oeffnen('neu')}>
          <Plus className="mr-2 h-4 w-4" /> Neue Landingpage
        </Button>
      </div>

      {laedt ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : seiten.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">Noch keine Landingpages. Lege die erste an.</CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="divide-y p-0">
            {seiten.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-medium">{s.title}</span>
                    <Badge variant={s.status === 'published' ? 'default' : 'secondary'} size="sm">
                      {s.status === 'published' ? 'Online' : s.hat_dokument ? 'Offline' : 'Noch nicht gestaltet'}
                    </Badge>
                    {s.noindex ? (
                      <Badge variant="outline" size="sm">
                        Nicht bei Google
                      </Badge>
                    ) : null}
                    {s.kampagne ? (
                      <Badge variant="outline" size="sm">
                        {s.kampagne}
                      </Badge>
                    ) : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
                    <a
                      href={`${WEBSITE_BASE_URL}/${s.pfad}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 hover:text-primary"
                    >
                      /{s.pfad} <ExternalLink className="h-3 w-3" />
                    </a>
                    <span>{[s.fach_name, s.standort_name].filter(Boolean).join(' · ') || 'Ohne Fach und Standort'}</span>
                  </div>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => void gestalten(s.id)}>
                  <LayoutTemplate className="mr-2 h-4 w-4" /> Gestalten
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => setAbTest(s)}>
                  <FlaskConical className="mr-2 h-4 w-4" /> A/B-Test
                  {s.aktive_varianten > 0 ? (
                    <Badge size="sm" className="ml-2">
                      läuft
                    </Badge>
                  ) : null}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => oeffnen(s)} aria-label={`Einstellungen von ${s.title}`}>
                  <Settings2 className="h-4 w-4" />
                </Button>
                {s.hat_dokument ? (
                  <Button type="button" variant="outline" size="sm" onClick={() => void umschalten(s)}>
                    {s.status === 'published' ? (
                      <>
                        <GlobeLock className="mr-2 h-4 w-4" /> Offline nehmen
                      </>
                    ) : (
                      <>
                        <Globe className="mr-2 h-4 w-4" /> Online stellen
                      </>
                    )}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setZuLoeschen(s)}
                  aria-label={`${s.title} löschen`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={dialog !== null} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{dialog === 'neu' ? 'Neue Landingpage' : 'Einstellungen'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="lp-titel">Titel</Label>
              <Input
                id="lp-titel"
                value={eingabe.title}
                onChange={(e) => setEingabe({ ...eingabe, title: e.target.value })}
                placeholder="z. B. Gitarre in Braunschweig – Herbst"
              />
            </div>
            {dialog === 'neu' ? (
              <div className="space-y-1.5">
                <Label htmlFor="lp-pfad">Pfad (URL)</Label>
                <Input
                  id="lp-pfad"
                  value={eingabe.slug}
                  onChange={(e) => setEingabe({ ...eingabe, slug: e.target.value })}
                  placeholder="leer = aus dem Titel"
                />
                <p className="text-xs text-muted-foreground">Erreichbar unter /lp/{pfad || 'pfad'} — später nicht mehr änderbar.</p>
              </div>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="lp-fach">Fach</Label>
                <select
                  id="lp-fach"
                  className={auswahl}
                  value={eingabe.fach_id ?? ''}
                  onChange={(e) => setEingabe({ ...eingabe, fach_id: e.target.value ? Number(e.target.value) : null })}
                >
                  <option value="">— keins —</option>
                  {ziele.faecher.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lp-standort">Standort</Label>
                <select
                  id="lp-standort"
                  className={auswahl}
                  value={eingabe.standort_id ?? ''}
                  onChange={(e) => setEingabe({ ...eingabe, standort_id: e.target.value ? Number(e.target.value) : null })}
                >
                  <option value="">— keiner —</option>
                  {ziele.standorte.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lp-kampagne">Kampagne (optional)</Label>
              <Input
                id="lp-kampagne"
                value={eingabe.kampagne ?? ''}
                onChange={(e) => setEingabe({ ...eingabe, kampagne: e.target.value || null })}
                placeholder="z. B. Herbst 2026"
              />
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={eingabe.noindex}
                onChange={(e) => setEingabe({ ...eingabe, noindex: e.target.checked })}
              />
              <span>
                Nicht bei Google anzeigen
                <span className="block text-xs text-muted-foreground">
                  Für Anzeigen- und Testseiten, damit sie nicht mit den Fach- und Standortseiten konkurrieren.
                </span>
              </span>
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setDialog(null)}>
              Abbrechen
            </Button>
            <Button type="button" loading={speichert} onClick={() => void speichern()} disabled={dialog === 'neu' && !pfad}>
              {dialog === 'neu' ? 'Anlegen' : 'Speichern'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={abTest !== null}
        onOpenChange={(o) => {
          if (!o) {
            setAbTest(null);
            void laden();
          }
        }}
      >
        <DialogContent size="full" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{abTest ? `A/B-Test: ${abTest.title}` : ''}</DialogTitle>
          </DialogHeader>
          {abTest ? <LandingpageVarianten key={abTest.id} seite={abTest} gestalten={(id) => void gestalten(id)} /> : null}
        </DialogContent>
      </Dialog>

      <LoeschenMitGrundDialog
        offen={zuLoeschen !== null}
        titel={`Landingpage „${zuLoeschen?.title ?? ''}“ löschen?`}
        onAbbrechen={() => setZuLoeschen(null)}
        onLoeschen={async (grund) => {
          if (zuLoeschen) await loeschen(zuLoeschen, grund);
        }}
      />
    </div>
  );
};

export default CmsLandingpagesPage;
